import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router';
import type { Session } from '@supabase/supabase-js';
import { ApiError } from '@crave/api-client';
import { RELEASE } from '@crave/shared';
import { Button, Card } from '@crave/ui';
import { api } from './api.ts';
import { Notice } from './components/ui.tsx';
import { authConfigured, onSession, signOut } from './lib/auth.ts';
import {
  STAFF_ME_KEY,
  StaffProvider,
  readSavedLocation,
  saveLocation,
  staffContextValue,
  type StaffMe,
} from './lib/staff.tsx';
import { AuditTrail } from './pages/AuditTrail.tsx';
import { Capacity } from './pages/Capacity.tsx';
import { CardReaders } from './pages/CardReaders.tsx';
import { CustomerDetail } from './pages/CustomerDetail.tsx';
import { Customers } from './pages/Customers.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Feedback } from './pages/Feedback.tsx';
import { Ingredients } from './pages/Ingredients.tsx';
import { Integrations } from './pages/Integrations.tsx';
import { Inventory } from './pages/Inventory.tsx';
import { Locations } from './pages/Locations.tsx';
import { Login } from './pages/Login.tsx';
import { Messages } from './pages/Messages.tsx';
import { useInboxLive, useInboxLiveState } from './lib/inboxLive.ts';
import { Loyalty } from './pages/Loyalty.tsx';
import { OrderDetail } from './pages/OrderDetail.tsx';
import { Orders } from './pages/Orders.tsx';
import { Outbox } from './pages/Outbox.tsx';
import { Purchasing } from './pages/Purchasing.tsx';
import { Readiness } from './pages/Readiness.tsx';
import { Recipes } from './pages/Recipes.tsx';
import { Reports } from './pages/Reports.tsx';
import { Security } from './pages/Security.tsx';
import { Shopping } from './pages/Shopping.tsx';
import { Staff } from './pages/Staff.tsx';
import { TastingDetail } from './pages/TastingDetail.tsx';
import { Tastings } from './pages/Tastings.tsx';

const NAV: { to: string; label: string; group: string; perm?: string }[] = [
  { to: '/', label: 'Dashboard', group: '', perm: 'reports.read' },
  { to: '/orders', label: 'Orders', group: '', perm: 'orders.read' },
  { to: '/capacity', label: 'Capacity', group: '', perm: 'capacity.manage' },
  { to: '/reports', label: 'Reports', group: '', perm: 'finance.read' },
  { to: '/messages', label: 'Messages', group: 'Guests', perm: 'messages.manage' },
  { to: '/customers', label: 'Customers', group: 'Guests', perm: 'customers.read' },
  { to: '/loyalty', label: 'Crave Credit', group: 'Guests', perm: 'loyalty.manage' },
  { to: '/feedback', label: 'Feedback', group: 'Guests', perm: 'feedback.read' },
  { to: '/tastings', label: 'Tastings', group: 'Guests', perm: 'tastings.manage' },
  { to: '/inventory', label: 'Inventory', group: 'Kitchen', perm: 'inventory.read' },
  { to: '/shopping', label: 'Shopping list', group: 'Kitchen', perm: 'shopping.manage' },
  { to: '/purchasing', label: 'Purchasing', group: 'Kitchen', perm: 'purchasing.manage' },
  { to: '/recipes', label: 'Recipes', group: 'Kitchen', perm: 'recipes.manage' },
  { to: '/ingredients', label: 'Ingredients', group: 'Kitchen', perm: 'recipes.manage' },
  { to: '/readiness', label: 'Menu, prices & hours', group: 'Setup', perm: 'locations.manage' },
  { to: '/settings/locations', label: 'Locations', group: 'Setup', perm: 'locations.manage' },
  { to: '/staff', label: 'Staff', group: 'Setup', perm: 'staff.manage' },
  { to: '/settings/readers', label: 'POS & card readers', group: 'Setup', perm: 'pos.readers' },
  { to: '/settings/integrations', label: 'Integrations', group: 'Setup', perm: 'settings.manage' },
  { to: '/settings/outbox', label: 'Outbox', group: 'Setup', perm: 'settings.manage' },
  { to: '/audit', label: 'Audit trail', group: 'Setup', perm: 'audit.read' },
  { to: '/settings/security', label: 'Security', group: 'Setup' },
];

/** Areas the API refuses without a two-factor session, keyed by the permission that opens them. */
const MFA_AREAS: { perm: string; label: string }[] = [
  { perm: 'settings.manage', label: 'payment and email settings' },
  { perm: 'loyalty.manage', label: 'Crave Credit rules' },
  { perm: 'finance.read', label: 'unit-economics numbers' },
];

const listJoin = (parts: string[]) =>
  parts.length <= 1
    ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const ROLE: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  team_member: 'Kitchen & counter',
  kitchen: 'Kitchen',
  cashier: 'Front counter',
};

type NavEntry = (typeof NAV)[number];

/** Consecutive menu entries grouped under their heading, in menu order. */
function navGroups(entries: NavEntry[]): [string, NavEntry[]][] {
  const groups: [string, NavEntry[]][] = [];
  for (const n of entries) {
    const last = groups[groups.length - 1];
    if (last && last[0] === n.group) last[1].push(n);
    else groups.push([n.group, [n]]);
  }
  return groups;
}

/** One or two letters for the signed-in person's round badge. */
function initials(name: string | null | undefined, email: string): string {
  const letters = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return letters || (email[0] ?? '?').toUpperCase();
}

/* Line icons in the spirit of SF Symbols: one 20px box, rounded 1.75px strokes. */
const ICONS: Record<string, ReactNode> = {
  '/': (
    <>
      <rect x="3" y="3" width="5.5" height="5.5" rx="1.5" />
      <rect x="11.5" y="3" width="5.5" height="5.5" rx="1.5" />
      <rect x="3" y="11.5" width="5.5" height="5.5" rx="1.5" />
      <rect x="11.5" y="11.5" width="5.5" height="5.5" rx="1.5" />
    </>
  ),
  '/orders': (
    <>
      <path d="M4.8 7h10.4l-.8 9a1.6 1.6 0 0 1-1.6 1.5H7.2A1.6 1.6 0 0 1 5.6 16L4.8 7Z" />
      <path d="M7.5 7V5.8a2.5 2.5 0 0 1 5 0V7" />
    </>
  ),
  '/capacity': (
    <>
      <path d="M3.2 14a6.8 6.8 0 1 1 13.6 0" />
      <path d="m10 14 3.2-4" />
      <path d="M3 17h14" />
    </>
  ),
  '/reports': <path d="M4 16.5v-5M8 16.5v-9M12 16.5v-7M16 16.5v-13" />,
  '/messages': (
    <path d="M10 3.5c3.9 0 7 2.6 7 5.8s-3.1 5.8-7 5.8c-.8 0-1.6-.1-2.3-.3L4 16.5l.9-3.2C3.7 12.3 3 10.9 3 9.3 3 6.1 6.1 3.5 10 3.5Z" />
  ),
  '/customers': (
    <>
      <circle cx="10" cy="7" r="3.2" />
      <path d="M3.8 17c.7-3 3.2-4.8 6.2-4.8s5.5 1.8 6.2 4.8" />
    </>
  ),
  '/loyalty': (
    <>
      <rect x="3" y="7.5" width="14" height="3.5" rx="1" />
      <path d="M4.5 11v5.5h11V11M10 7.5v9" />
      <path d="M10 7.5C8.6 4.6 5.6 4.5 5.8 6.3 6 7.4 8.4 7.5 10 7.5Zm0 0c1.4-2.9 4.4-3 4.2-1.2-.2 1.1-2.6 1.2-4.2 1.2Z" />
    </>
  ),
  '/feedback': (
    <path d="m10 3 2.2 4.4 4.8.7-3.5 3.4.8 4.8L10 14l-4.3 2.3.8-4.8L3 8.1l4.8-.7L10 3Z" />
  ),
  '/tastings': (
    <>
      <rect x="3" y="4.5" width="14" height="12.5" rx="2.2" />
      <path d="M3 8.5h14M7 2.8v3.4M13 2.8v3.4" />
    </>
  ),
  '/inventory': (
    <>
      <path d="M3.2 6.3 10 3.2l6.8 3.1v7.4L10 16.8l-6.8-3.1V6.3Z" />
      <path d="M3.2 6.3 10 9.4l6.8-3.1M10 9.4v7.4" />
    </>
  ),
  '/purchasing': (
    <>
      <path d="M2.5 3.5h2.2l1.8 9h8.6l1.6-6.3H5.3" />
      <circle cx="8" cy="16" r="1.1" />
      <circle cx="14" cy="16" r="1.1" />
    </>
  ),
  '/recipes': (
    <>
      <path d="M10 5.2C8.6 4 6.4 3.6 3.5 3.8v11.6c2.9-.2 5.1.2 6.5 1.4 1.4-1.2 3.6-1.6 6.5-1.4V3.8c-2.9-.2-5.1.2-6.5 1.4Z" />
      <path d="M10 5.2v11.6" />
    </>
  ),
  '/ingredients': (
    <>
      <path d="M16.5 3.5C9.5 3.5 4 6.8 4 12c0 2.4 1.6 4.2 4 4.2 5.2 0 8.5-5.5 8.5-12.7Z" />
      <path d="M3.5 17 11 9.5" />
    </>
  ),
  '/readiness': (
    <>
      <path d="M3.2 9.8V4.2a1 1 0 0 1 1-1h5.6a1 1 0 0 1 .7.3l6.2 6.2a1 1 0 0 1 0 1.4l-5.6 5.6a1 1 0 0 1-1.4 0L3.5 10.5a1 1 0 0 1-.3-.7Z" />
      <circle cx="7" cy="7" r="1.1" />
    </>
  ),
  '/staff': (
    <>
      <circle cx="7.5" cy="7.5" r="2.7" />
      <path d="M2.5 16.5c.5-2.6 2.5-4.1 5-4.1s4.5 1.5 5 4.1" />
      <path d="M12.8 5a2.7 2.7 0 0 1 0 5M14.6 12.6c1.5.5 2.5 1.8 2.9 3.9" />
    </>
  ),
  '/settings/integrations': (
    <>
      <path d="M8.6 11.4a3.2 3.2 0 0 0 4.5 0l2.6-2.6a3.2 3.2 0 0 0-4.5-4.5l-1 1" />
      <path d="M11.4 8.6a3.2 3.2 0 0 0-4.5 0l-2.6 2.6a3.2 3.2 0 0 0 4.5 4.5l1-1" />
    </>
  ),
  '/settings/outbox': (
    <>
      <path d="M17 3 2.8 8.7l6 2.5L11.3 17 17 3Z" />
      <path d="M8.8 11.2 17 3" />
    </>
  ),
  '/shopping': (
    <>
      <path d="M2.8 3.5h2l1.9 9.2a1.4 1.4 0 0 0 1.4 1.1h6.3a1.4 1.4 0 0 0 1.4-1.1l1.2-5.9H5.6" />
      <circle cx="8.3" cy="16.8" r="1" />
      <circle cx="14.2" cy="16.8" r="1" />
    </>
  ),
  '/settings/locations': (
    <>
      <path d="M10 17.2s-5.2-4.6-5.2-8.8a5.2 5.2 0 0 1 10.4 0c0 4.2-5.2 8.8-5.2 8.8Z" />
      <circle cx="10" cy="8.4" r="1.9" />
    </>
  ),
  '/settings/readers': (
    <>
      <rect x="5" y="2.8" width="10" height="14.4" rx="2.2" />
      <path d="M8 6.5h4M8 13.5h4" />
    </>
  ),
  '/audit': (
    <>
      <path d="M3.6 10a6.4 6.4 0 1 0 1.9-4.5" />
      <path d="M3.3 3.2v3h3" />
      <path d="M10 6.4V10l2.4 1.6" />
    </>
  ),
  '/settings/security': (
    <>
      <rect x="4" y="8.8" width="12" height="8.4" rx="2.2" />
      <path d="M6.8 8.8V6.3a3.2 3.2 0 0 1 6.4 0v2.5" />
    </>
  ),
};

function NavIcon({ to }: { to: string }) {
  return (
    <svg
      className="crave-nav-icon"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[to] ?? <circle cx="10" cy="10" r="6.5" />}
    </svg>
  );
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => onSession(setSession), []);
  if (authConfigured && session === undefined) return null;
  if (!authConfigured || !session) return <Login />;
  return <Shell email={session.user.email ?? ''} userId={session.user.id} />;
}

function Shell({ email, userId }: { email: string; userId: string }) {
  // Phones: the menu folds behind a button and closes after every navigation.
  const [navOpen, setNavOpen] = useState(false);
  // The location every page works on, remembered on this device.
  const [locationSlug, setLocationSlug] = useState(readSavedLocation);
  const chooseLocation = (slug: string) => {
    saveLocation(slug);
    setLocationSlug(slug);
  };
  const { pathname } = useLocation();
  useEffect(() => setNavOpen(false), [pathname]);
  const me = useQuery({
    queryKey: [STAFF_ME_KEY, userId],
    queryFn: async () => (await api.get<StaffMe>('/staff/me')).data,
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && count < 2,
  });
  // New customer texts light up Messages in the sidebar, for staff who can read them. Updates
  // arrive live; the slow check is only the fallback while the live connection is down.
  const canText = Boolean(me.data?.permissions.includes('messages.manage'));
  useInboxLive(canText);
  const live = useInboxLiveState();
  const unreadTexts = useQuery({
    queryKey: ['messages-unread'],
    queryFn: async () => (await api.get<{ unread: number }>('/admin/messages/unread')).data.unread,
    enabled: canText,
    refetchInterval: live === 'live' ? 120_000 : 20_000,
  });

  if (me.isError) {
    const notStaff = me.error instanceof ApiError && me.error.status === 403;
    return (
      <main style={{ maxWidth: 480, margin: '10vh auto', padding: 24, display: 'grid', gap: 16 }}>
        <img
          src="/brand/logo-color-480.png"
          alt="Crave Pizza"
          style={{ height: 56, width: 'auto', justifySelf: 'start' }}
        />
        <Card>
          <p style={{ margin: 0 }}>{describeStaffError(me.error)}</p>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 14 }}>
            Signed in as {email}.
          </p>
        </Card>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>
          {!notStaff && <Button onClick={() => void me.refetch()}>Try again</Button>}
        </div>
      </main>
    );
  }
  if (!me.data) return <p style={{ padding: 32 }}>Loading…</p>;

  const staff = staffContextValue(me.data, locationSlug, chooseLocation);
  const perms = staff.permissions;
  const visible = NAV.filter((n) => !n.perm || perms.has(n.perm));
  const mfaAreas = MFA_AREAS.filter((a) => perms.has(a.perm)).map((a) => a.label);
  const needsMfa = staff.needsMfa && mfaAreas.length > 0;
  const role = me.data.roles.map((r) => ROLE[r.role] ?? r.role).join(', ');
  const unread = unreadTexts.data ?? 0;

  return (
    <StaffProvider value={staff}>
      <div className="crave-shell">
        <nav className="crave-nav" data-open={navOpen ? 'true' : 'false'} aria-label="Admin">
          <div className="crave-nav-top">
            <Link to="/" className="crave-nav-logo">
              <img src="/brand/logo-color-480.png" alt="Crave Pizza admin" />
            </Link>
            <button
              type="button"
              className="crave-nav-toggle"
              aria-expanded={navOpen}
              aria-controls="crave-nav-items"
              onClick={() => setNavOpen((o) => !o)}
            >
              {navOpen ? 'Close' : 'Menu'}
            </button>
          </div>
          <div className="crave-nav-panel" id="crave-nav-items">
            <div className="crave-nav-scroll">
              {staff.locations.length > 1 && (
                <label className="crave-nav-location">
                  <span className="crave-nav-label">Location</span>
                  <select
                    value={staff.location.slug}
                    onChange={(e) => chooseLocation(e.target.value)}
                  >
                    {staff.locations.map((l) => (
                      <option key={l.id} value={l.slug}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {navGroups(visible).map(([group, items]) => (
                <div className="crave-nav-group" key={group || 'main'}>
                  {group && <p className="crave-nav-label">{group}</p>}
                  {items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.to === '/'} className="crave-nav-link">
                      <NavIcon to={n.to} />
                      <span className="crave-nav-text">{n.label}</span>
                      {n.to === '/messages' && unread > 0 && (
                        <span className="crave-nav-badge" aria-label={`${unread} unread`}>
                          {unread > 99 ? '99+' : unread}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              ))}
            </div>
            <div className="crave-nav-foot">
              <div className="crave-nav-person">
                <span className="crave-nav-avatar" aria-hidden="true">
                  {initials(me.data.display_name, email)}
                </span>
                <div className="crave-nav-who">
                  <span className="crave-nav-name">{me.data.display_name}</span>
                  <span className="crave-nav-meta" title={email}>
                    {role} · {email}
                  </span>
                </div>
              </div>
              <div className="crave-nav-foot-row">
                <span className="crave-nav-release">Crave {RELEASE}</span>
                <button type="button" className="crave-nav-signout" onClick={() => void signOut()}>
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </nav>
        <main className="crave-main">
          {needsMfa && (
            <div className="crave-mfa">
              <Notice kind="error">
                Two-factor sign-in is required for {listJoin(mfaAreas)}.{' '}
                <Link to="/settings/security">Open Security</Link> to set it up or verify this
                session.
              </Notice>
            </div>
          )}
          <Routes>
            <Route path="/" element={perms.has('reports.read') ? <Dashboard /> : <Orders />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:id" element={<OrderDetail />} />
            <Route path="/capacity" element={<Capacity />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/messages/:id" element={<Messages />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id" element={<CustomerDetail />} />
            <Route path="/loyalty" element={<Loyalty />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/tastings" element={<Tastings />} />
            <Route path="/tastings/:id" element={<TastingDetail />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/shopping" element={<Shopping />} />
            <Route path="/purchasing" element={<Purchasing />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/ingredients" element={<Ingredients />} />
            <Route path="/readiness" element={<Readiness />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/settings/readers" element={<CardReaders />} />
            <Route path="/settings/locations" element={<Locations />} />
            <Route path="/settings/integrations" element={<Integrations />} />
            <Route path="/settings/outbox" element={<Outbox />} />
            <Route path="/audit" element={<AuditTrail />} />
            <Route path="/settings/security" element={<Security />} />
            <Route path="*" element={perms.has('reports.read') ? <Dashboard /> : <Orders />} />
          </Routes>
        </main>
      </div>
    </StaffProvider>
  );
}

function describeStaffError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return err.message;
    const ref = err.requestId ? `, ref ${err.requestId}` : '';
    if (err.status === 401)
      return `The Crave server didn't accept this sign-in (${err.message}${ref}). Sign out, then sign in again.`;
    return `The Crave server returned an error: ${err.message} (HTTP ${err.status}${ref}).`;
  }
  const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
  return `Couldn't reach the Crave server${detail}. Check the connection and try again.`;
}
